import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Upload, Send, Briefcase, TrendingUp, Home as HomeIcon, Wrench, ShieldCheck, DollarSign } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, MaskLine } from "@/components/Reveal";
import { scrollToId } from "@/components/SmoothScroll";
import api, { formatApiErrorDetail, fileToDataUrl } from "@/lib/api";
import { JOBS, HIRING_STEPS, COMPANY } from "@/lib/content";
import usePageMeta from "@/lib/useMeta";

const PERKS = [
  { icon: DollarSign, t: "Competitive Pay", d: "Compensation based on position, experience, and performance." },
  { icon: TrendingUp, t: "Career Advancement", d: "We promote from within into foreman, superintendent, and PM roles." },
  { icon: Briefcase, t: "Consistent Opportunities", d: "Expanding fiber operations create steady work for qualified crews." },
  { icon: HomeIcon, t: "Housing for Travel", d: "Company-arranged housing may be available on qualifying projects." },
  { icon: Wrench, t: "Modern Equipment", d: "Professional equipment, vehicles, and tools for underground work." },
  { icon: ShieldCheck, t: "Safety-Focused Culture", d: "OSHA-aligned policies and daily safety on every job." },
];

const EMPTY = {
  type: "application", full_name: "", phone: "", email: "", location: "", position: "",
  years_construction: "", years_fiber: "", equipment_operated: "", has_license: "",
  has_cdl: "", willing_travel: "", available_overnight: "", desired_pay: "", start_date: "",
  experience_notes: "", resume_file: "", resume_name: "", license_file: "", license_name: "", hp: "",
};

function TextField({ label, value, onChange, testid, type = "text", required }) {
  return (
    <div>
      <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">{label}{required && <span className="text-wcc-red"> *</span>}</label>
      <input type={type} value={value} onChange={onChange} data-testid={testid} required={required}
        className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
    </div>
  );
}

function ApplicationForm({ presetPosition, onDone }) {
  const [form, setForm] = useState({ ...EMPTY, position: presetPosition || "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (key, nameKey) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) return toast.error("File too large (max 6MB).");
    const data = await fileToDataUrl(file);
    setForm((f) => ({ ...f, [key]: data, [nameKey]: file.name }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name) return toast.error("Please enter your full name.");
    setLoading(true);
    try {
      await api.post("/applications", form);
      toast.success("Application submitted! Our team will contact you when a matching role opens.");
      setForm({ ...EMPTY, position: presetPosition || "" });
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-wcc-ink/10 p-8 sm:p-10" data-testid="application-form">
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
        value={form.hp} onChange={set("hp")} style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} />
      <div className="grid sm:grid-cols-2 gap-5">
        <TextField label="Full Name" required value={form.full_name} onChange={set("full_name")} testid="app-full-name" />
        <TextField label="Phone Number" value={form.phone} onChange={set("phone")} testid="app-phone" />
        <TextField label="Email Address" type="email" value={form.email} onChange={set("email")} testid="app-email" />
        <TextField label="City & State" value={form.location} onChange={set("location")} testid="app-location" />
        <TextField label="Position Applying For" value={form.position} onChange={set("position")} testid="app-position" />
        <TextField label="Desired Pay Rate" value={form.desired_pay} onChange={set("desired_pay")} testid="app-pay" />
        <TextField label="Years Construction Experience" value={form.years_construction} onChange={set("years_construction")} testid="app-years-construction" />
        <TextField label="Years Underground Fiber Experience" value={form.years_fiber} onChange={set("years_fiber")} testid="app-years-fiber" />
        <TextField label="Valid Driver’s License? (Y/N)" value={form.has_license} onChange={set("has_license")} testid="app-license" />
        <TextField label="Do You Have a CDL? (Y/N)" value={form.has_cdl} onChange={set("has_cdl")} testid="app-cdl" />
        <TextField label="Willing to Travel? (Y/N)" value={form.willing_travel} onChange={set("willing_travel")} testid="app-travel" />
        <TextField label="Available for Overnight? (Y/N)" value={form.available_overnight} onChange={set("available_overnight")} testid="app-overnight" />
        <TextField label="Earliest Start Date" value={form.start_date} onChange={set("start_date")} testid="app-start-date" />
        <TextField label="Equipment You Have Operated" value={form.equipment_operated} onChange={set("equipment_operated")} testid="app-equipment" />
      </div>

      <div className="mt-5">
        <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Briefly Describe Your Experience</label>
        <textarea value={form.experience_notes} onChange={set("experience_notes")} rows={4} data-testid="app-notes"
          className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink resize-none transition-colors" />
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-5">
        <FileUpload label="Upload Résumé" name={form.resume_name} onChange={onFile("resume_file", "resume_name")} testid="app-resume" />
        <FileUpload label="Upload License / Certifications" name={form.license_name} onChange={onFile("license_file", "license_name")} testid="app-license-file" />
      </div>

      <button type="submit" disabled={loading} data-testid="app-submit"
        className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-wcc-red text-white font-semibold px-6 py-4 rounded-sm hover:bg-wcc-red-deep disabled:opacity-60 transition-colors">
        {loading ? "Submitting…" : <>Submit Application <Send className="w-4 h-4" /></>}
      </button>
    </form>
  );
}

function FileUpload({ label, name, onChange, testid }) {
  return (
    <label className="block cursor-pointer" data-testid={`${testid}-wrap`}>
      <span className="mono text-[10px] uppercase tracking-widest text-wcc-gray">{label}</span>
      <div className="mt-2 flex items-center gap-3 border-2 border-dashed border-wcc-ink/20 hover:border-wcc-red px-4 py-3 transition-colors">
        <Upload className="w-4 h-4 text-wcc-red" />
        <span className="text-sm text-wcc-ink/70 truncate">{name || "Choose file (PDF / image, max 6MB)"}</span>
      </div>
      <input type="file" className="hidden" accept=".pdf,image/*" onChange={onChange} data-testid={testid} />
    </label>
  );
}

export default function Careers() {
  usePageMeta(
    "Careers | West Coral Communications — Underground Fiber Construction Jobs",
    "Join West Coral Communications. Now hiring foremen, cable plow & missile boring operators, trench crews, laborers, CDL drivers and restoration crews for underground fiber construction across KY, OH, FL and the Southeast. Apply online."
  );
  const [preset, setPreset] = useState("");
  const scrollToApply = (pos) => {
    setPreset(pos);
    setTimeout(() => scrollToId("apply"), 60);
  };

  return (
    <div className="pt-[74px]">
      {/* Hero */}
      <section className="relative bg-wcc-navy text-white py-24 sm:py-32 overflow-hidden" data-testid="careers-hero">
        <img src="/assets/field-3.webp" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-wcc-navy via-wcc-navy/85 to-wcc-navy/70" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8">
          <p className="mono text-[11px] uppercase tracking-[0.3em] text-wcc-red mb-6">Careers</p>
          <h1 className="font-display text-4xl sm:text-6xl md:text-7xl font-semibold tracking-tighter uppercase leading-[0.92]">
            <MaskLine delay={0.1}>Build America’s</MaskLine>
            <MaskLine delay={0.22} className="text-wcc-red">communication</MaskLine>
            <MaskLine delay={0.34}>infrastructure.</MaskLine>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
            We’re looking for dependable, safety-minded professionals across {COMPANY.regions.join(", ")}. Whether
            you’re a seasoned operator or ready to learn the industry, there’s a place for you on our crews.
          </p>
          <button onClick={() => scrollToApply("")} data-testid="careers-apply-top"
            className="mt-9 inline-flex items-center gap-2 bg-wcc-red text-white font-semibold px-7 py-3.5 rounded-sm shadow-brut hover:bg-wcc-red-deep hover:-translate-y-0.5 transition-[transform,background-color]">
            Apply Now <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Perks */}
      <section className="bg-wcc-paper py-24" data-testid="careers-perks">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <p className="mono text-[11px] uppercase tracking-[0.3em] text-wcc-red mb-10">Why Work With Us</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PERKS.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.05}>
                <div className="bg-white border border-wcc-ink/10 p-8 h-full hover:border-wcc-red transition-colors">
                  <div className="w-12 h-12 grid place-items-center bg-wcc-navy text-white mb-6"><p.icon className="w-6 h-6" /></div>
                  <h3 className="font-display text-xl font-medium text-wcc-ink mb-2">{p.t}</h3>
                  <p className="text-sm text-wcc-ink/65 leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Openings */}
      <section className="bg-white py-24 border-y border-wcc-ink/10" data-testid="careers-openings">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8">
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter uppercase leading-none text-wcc-ink mb-12">
            Current <span className="text-wcc-red">Openings</span>
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {JOBS.map((job) => (
              <AccordionItem key={job.id} value={job.id} className="border-b border-wcc-ink/15" data-testid={`job-${job.id}`}>
                <AccordionTrigger className="text-left hover:no-underline py-6 group" data-testid={`job-trigger-${job.id}`}>
                  <span className="font-display text-xl sm:text-2xl font-medium text-wcc-ink group-hover:text-wcc-red transition-colors pr-4">
                    {job.title}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-8">
                  <p className="text-wcc-ink/75 leading-relaxed mb-6">{job.blurb}</p>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <p className="mono text-[10px] uppercase tracking-widest text-wcc-red mb-3">Primary Responsibilities</p>
                      <ul className="space-y-2">
                        {job.responsibilities.map((r) => (
                          <li key={r} className="flex items-start gap-2.5 text-sm text-wcc-ink/75">
                            <span className="mt-1.5 h-1.5 w-1.5 bg-wcc-red shrink-0" />{r}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="mono text-[10px] uppercase tracking-widest text-wcc-red mb-3">Preferred Qualifications</p>
                      <ul className="space-y-2">
                        {job.qualifications.map((q) => (
                          <li key={q} className="flex items-start gap-2.5 text-sm text-wcc-ink/75">
                            <span className="mt-1.5 h-1.5 w-1.5 bg-wcc-navy shrink-0" />{q}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <button onClick={() => scrollToApply(job.title)} data-testid={`job-apply-${job.id}`}
                    className="mt-7 inline-flex items-center gap-2 bg-wcc-navy text-white font-semibold px-6 py-3 rounded-sm hover:bg-wcc-red transition-colors">
                    Apply for this position <ArrowUpRight className="w-4 h-4" />
                  </button>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Hiring process */}
      <section className="bg-wcc-navy text-white py-24" data-testid="careers-process">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <p className="mono text-[11px] uppercase tracking-[0.3em] text-wcc-red mb-10">Our Hiring Process</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {HIRING_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.05} className="bg-wcc-navy">
                <div className="p-7 h-full">
                  <span className="ghost-number text-5xl block mb-4">{s.n}</span>
                  <h3 className="font-display text-lg font-medium mb-2">{s.t}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="apply" className="bg-wcc-paper py-24" data-testid="careers-apply-section">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8">
          <div className="mb-10">
            <p className="mono text-[11px] uppercase tracking-[0.3em] text-wcc-red mb-4">Employment Application</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-wcc-ink">
              Apply to join West Coral Communications
            </h2>
            <p className="mt-3 text-wcc-ink/65 max-w-2xl">
              Complete the application and a member of our team will contact you when a position matching your
              qualifications becomes available.
            </p>
          </div>
          <ApplicationForm presetPosition={preset} />
        </div>
      </section>
    </div>
  );
}
